/*
 * @Copyright 2024-2025. Institute for Future Intelligence, Inc.
 */

import { useEffect, useMemo } from 'react';
import { App } from 'antd';
import { FirebaseError } from 'firebase/app';
import {
  GoogleAuthProvider,
  User as FirebaseUser,
  getRedirectResult,
  linkWithPopup,
  linkWithRedirect,
  signInAnonymously,
  signInWithPopup,
  signInWithRedirect,
  signOut,
} from 'firebase/auth';
import { collection, doc, getCountFromServer, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useStore } from './stores/common';
import { usePrimitiveStore } from './stores/commonPrimitive';
import * as Selector from './stores/selector';
import { auth, firestore } from './firebase.ts';
import { setMessage } from './helpers';
import { Util } from './Util';
import { User } from './User';
import { NO_NAME } from './screenName.ts';

// Best-effort error → human string. FirebaseError carries .message and .code;
// generic Errors carry .message; unknowns fall back to String(e).
const formatAuthError = (e: unknown): string => {
  if (e && typeof e === 'object') {
    const msg = (e as { message?: unknown }).message;
    if (typeof msg === 'string' && msg) return msg;
    const code = (e as { code?: unknown }).code;
    if (typeof code === 'string' && code) return code;
  }
  return String(e);
};

// Pulls a Firestore user record into the common store, or creates one on
// first sign-in. The superuser path also fetches the global user count.
const registerUser = async (
  user: User,
  setCommonStore: (fn: (state: any) => void) => void,
  t: ReturnType<typeof useTranslation>['t'],
  lang: { lng: string },
) => {
  let noLogging = false;
  let anonymous = false;
  let likes: string[] = [];
  let published: string[] = [];
  let aliases: string[] = [];
  let acceptedTermsAt: string | undefined;
  let acceptedTermsVersion: string | undefined;
  let found = false;
  let userCount = 0;
  if (user.uid !== null) {
    const superuser = user && user.email === 'charles@intofuture.org';
    if (superuser) {
      try {
        const snapshot = await getCountFromServer(collection(firestore, 'users'));
        userCount = snapshot.data().count;
      } catch (e) {
        console.warn('Failed to count users:', e);
      }
    }

    const docSnap = await getDoc(doc(firestore, 'users', user.uid));
    const docData = docSnap.data();
    if (docData) {
      noLogging = !!docData.noLogging;
      anonymous = !!docData.anonymous;
      if (docData.likes) likes = docData.likes;
      if (docData.published) published = docData.published;
      if (docData.aliases) aliases = docData.aliases;
      acceptedTermsAt = typeof docData.acceptedTermsAt === 'string' ? docData.acceptedTermsAt : undefined;
      acceptedTermsVersion =
        typeof docData.acceptedTermsVersion === 'string' ? docData.acceptedTermsVersion : undefined;
      found = true;
    }
  }
  // The pre-sign-in gate stashes the user's acceptance here so we can
  // record it on the Firestore user document as soon as auth completes.
  const pending = usePrimitiveStore.getState().pendingAcceptance;

  if (found) {
    // Promote the stashed acceptance if (a) we have one and (b) the stored
    // version is missing or older than the one the user just agreed to.
    let nextAcceptedAt = acceptedTermsAt;
    let nextAcceptedVersion = acceptedTermsVersion;
    if (user.uid && pending && pending.acceptedTermsVersion !== acceptedTermsVersion) {
      try {
        await updateDoc(doc(firestore, 'users', user.uid), {
          acceptedTermsAt: pending.acceptedTermsAt,
          acceptedTermsVersion: pending.acceptedTermsVersion,
        });
        nextAcceptedAt = pending.acceptedTermsAt;
        nextAcceptedVersion = pending.acceptedTermsVersion;
      } catch (e) {
        console.warn('Failed to record terms acceptance:', e);
      }
    }
    setCommonStore((state) => {
      state.user.noLogging = noLogging;
      state.user.anonymous = anonymous;
      state.user.likes = likes;
      state.user.published = published;
      state.user.aliases = aliases;
      state.user.acceptedTermsAt = nextAcceptedAt;
      state.user.acceptedTermsVersion = nextAcceptedVersion;
    });
    usePrimitiveStore.getState().set((state) => {
      state.userCount = userCount;
      state.pendingAcceptance = null;
    });
    user.noLogging = noLogging;
    user.anonymous = anonymous;
    user.likes = likes;
    user.published = published;
    user.aliases = aliases;
    user.acceptedTermsAt = nextAcceptedAt;
    user.acceptedTermsVersion = nextAcceptedVersion;
  } else if (user.uid) {
    try {
      await setDoc(doc(firestore, 'users', user.uid), {
        uid: user.uid,
        noLogging: !!user.noLogging,
        anonymous: !!user.anonymous,
        since: dayjs(new Date()).format('MM/DD/YYYY hh:mm A'),
        os: Util.getOS(),
        // Pending acceptance is expected here — the pre-sign-in gate runs
        // before the auth call. If somehow missing, the user record is
        // created without acceptance, and the next sign-in attempt will
        // present the gate again.
        ...(pending
          ? {
              acceptedTermsAt: pending.acceptedTermsAt,
              acceptedTermsVersion: pending.acceptedTermsVersion,
            }
          : {}),
      });
      if (pending) {
        setCommonStore((state) => {
          state.user.acceptedTermsAt = pending.acceptedTermsAt;
          state.user.acceptedTermsVersion = pending.acceptedTermsVersion;
        });
      }
      usePrimitiveStore.getState().set((state) => {
        state.pendingAcceptance = null;
      });
      setMessage('info', t('message.YourAccountWasCreated', lang));
    } catch (e) {
      setMessage('error', t('message.CannotCreateAccount', lang) + ': ' + formatAuthError(e));
    }
  }
};

export interface AuthHandlers {
  signIn: () => Promise<void>;
  signInAnonymously: () => Promise<void>;
  signOut: () => Promise<void>;
}

export const useAuthHandlers = (): AuthHandlers => {
  const setCommonStore = useStore(Selector.set);
  const language = useStore(Selector.language);
  const { t } = useTranslation();
  const { modal } = App.useApp();
  const lang = useMemo(() => ({ lng: language }), [language]);

  // Run registerUser outside the immer producer so its async setCommonStore
  // calls don't re-enter the current one. Errors are surfaced to the user.
  const runRegisterUser = (snapshot: User) => {
    registerUser(snapshot, setCommonStore, t, lang).catch((e) => {
      setMessage('error', t('message.CannotCreateAccount', lang) + ': ' + formatAuthError(e));
    });
  };

  // Apply a freshly-authenticated Google user to the common store and trigger
  // Firestore record load/create. Shared between popup-success and the
  // post-redirect resolution path.
  const applyGoogleSignedInUser = (firebaseUser: FirebaseUser) => {
    setCommonStore((state) => {
      state.user.uid = firebaseUser.uid;
      state.user.email = firebaseUser.email;
      state.user.displayName = firebaseUser.displayName;
      state.user.photoURL = firebaseUser.photoURL;
    });
    runRegisterUser({ ...useStore.getState().user });
  };

  // Apply a successful anonymous→Google link. The Firebase UID is preserved,
  // so all projects under /users/{uid} stay with the user; we just refresh
  // the Google identity in-store and flip `anonymous` in the Firestore record.
  const applyLinkedUpgrade = (firebaseUser: FirebaseUser) => {
    if (firebaseUser.uid) {
      updateDoc(doc(firestore, 'users', firebaseUser.uid), { anonymous: false }).catch((e) => {
        // Non-fatal: in-memory state is already updated below; the Firestore
        // flag will catch up next time registerUser runs.
        console.warn('Failed to flip anonymous flag in Firestore:', e);
      });
    }
    setCommonStore((state) => {
      state.user.email = firebaseUser.email;
      state.user.displayName = firebaseUser.displayName;
      state.user.photoURL = firebaseUser.photoURL;
      state.user.anonymous = false;
    });
    setMessage('info', t('message.YourAccountWasUpgraded', lang));
  };

  // Resolve any post-redirect sign-in result on mount. getRedirectResult only
  // returns a non-null result the first time after a redirect roundtrip; on
  // subsequent calls it resolves to null, so this is safe to run unconditionally.
  useEffect(() => {
    getRedirectResult(auth)
      .then((result) => {
        if (!result?.user) return;
        // operationType is 'link' after linkWithRedirect, 'signIn' after
        // signInWithRedirect — branch so the link path doesn't run registerUser
        // (which would reload the Firestore record's stale `anonymous: true`).
        if (result.operationType === 'link') {
          applyLinkedUpgrade(result.user);
        } else {
          applyGoogleSignedInUser(result.user);
        }
      })
      .catch((e) => {
        setMessage('error', t('message.CannotSignIn', lang) + ': ' + formatAuthError(e));
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSignInAnonymously = async () => {
    try {
      const result = await signInAnonymously(auth);
      if (!result.user) return;
      setCommonStore((state) => {
        state.user.uid = result.user.uid;
        state.user.anonymous = true;
        state.user.displayName = NO_NAME;
      });
      runRegisterUser({ ...useStore.getState().user });
      // An anonymous account arrives with no name at all, so it reaches a class
      // roster as "Anonymous". Offer a screen name right here, while the user is
      // still thinking about who they are signing in as. Skippable.
      usePrimitiveStore.getState().set((state) => {
        state.askForScreenName = true;
      });
    } catch (e) {
      const error = e as FirebaseError;
      if (error.code !== 'auth/popup-closed-by-user' && error.code !== 'auth/cancelled-popup-request') {
        setMessage('error', t('message.CannotSignIn', lang) + ': ' + formatAuthError(error));
      }
    }
  };

  const handleSignIn = async () => {
    const provider = new GoogleAuthProvider();
    const currentUser = auth.currentUser;
    // If the user is already signed in anonymously, upgrade in place. This
    // preserves the Firebase UID, so projects under /users/{uid} are kept.
    const upgrading = !!currentUser?.isAnonymous;

    try {
      const result = upgrading ? await linkWithPopup(currentUser!, provider) : await signInWithPopup(auth, provider);
      if (!result.user) return;
      if (upgrading) applyLinkedUpgrade(result.user);
      else applyGoogleSignedInUser(result.user);
    } catch (e) {
      const error = e as FirebaseError;
      // Existing Google account already in Firebase — can't auto-merge without
      // risking data loss. Tell the user to sign out and sign in directly.
      if (
        upgrading &&
        (error.code === 'auth/credential-already-in-use' || error.code === 'auth/email-already-in-use')
      ) {
        // Modal (not toast) — message is long and carries action instructions
        modal.warning({
          title: t('avatarMenu.UpgradeToGoogle', lang),
          content: t('message.AnonymousLinkAccountExists', lang),
          okText: t('word.OK', lang),
        });
        return;
      }
      // Mobile Safari, in-app browsers, and some PWA contexts can't open popups.
      // Fall back to redirect; the post-redirect result is picked up by the
      // getRedirectResult effect above on the subsequent app load.
      if (error.code === 'auth/popup-blocked' || error.code === 'auth/operation-not-supported-in-this-environment') {
        try {
          if (upgrading) await linkWithRedirect(currentUser!, provider);
          else await signInWithRedirect(auth, provider);
          return;
        } catch (redirectErr) {
          setMessage('error', t('message.CannotSignIn', lang) + ': ' + formatAuthError(redirectErr));
          return;
        }
      }
      if (error.code !== 'auth/popup-closed-by-user' && error.code !== 'auth/cancelled-popup-request') {
        setMessage('error', t('message.CannotSignIn', lang) + ': ' + formatAuthError(error));
      }
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      setCommonStore((state) => {
        state.user.uid = null;
        state.user.email = null;
        state.user.displayName = null;
        state.user.photoURL = null;
        state.user.likes = [];
        state.user.published = [];
        state.user.aliases = [];
      });
      usePrimitiveStore.getState().set((state) => {
        state.showProjectListPanel = false;
      });
    } catch (e) {
      setMessage('error', t('message.CannotSignOut', lang) + ': ' + formatAuthError(e));
    }
  };

  return {
    signIn: handleSignIn,
    signInAnonymously: handleSignInAnonymously,
    signOut: handleSignOut,
  };
};
